import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { StorageService, LocationTypeEnum, DEFAULT_GEO_LOCATION_APPLIED_STORAGE_KEY, isDefined } from "@perxis-auto/shared";
import { getFiltersQueryValues } from "../../../features/filterChoice/lib/getFiltersQueryValues.js";
import { useFilterValues } from "../../../features/filterValues/lib/api/hooks/useFilterValues.js";
import { APP } from "../../../shared/config/app.js";
import "../../../shared/config/env.js";
import "../../../shared/config/events.js";
import { useQueryParams } from "../../../shared/lib/query/hooks/useQueryParams.js";
import "../../../shared/lib/store/createAppApi.js";
import "../../../shared/lib/store/createAppAsyncThunk.js";
import "../../../shared/lib/store/createAppSelector.js";
import "../../../shared/lib/store/createAppSlice.js";
import "redux";
import { useAppDispatch } from "../../../shared/lib/store/hooks/useAppDispatch.js";
import { useAppSelector } from "../../../shared/lib/store/hooks/useAppSelector.js";
import "../../../shared/lib/store/hooks/useAppStore.js";
import "../../../shared/store/config/model/selectors/selectBenefits.js";
import "../../../shared/store/config/model/selectors/selectBooking.js";
import "../../../shared/store/config/model/selectors/selectCredit.js";
import { selectFilterGrid } from "../../../shared/store/config/model/selectors/selectFilterGrid.js";
import { selectFilterGridItems } from "../../../shared/store/config/model/selectors/selectFilterGridItems.js";
import { selectFilters } from "../../../shared/store/config/model/selectors/selectFilters.js";
import { selectGeo } from "../../../shared/store/config/model/selectors/selectGeo.js";
import "../../../shared/store/config/model/selectors/selectGeolocator.js";
import "../../../shared/store/config/model/selectors/selectLocationCollectionIds.js";
import "../../../shared/store/config/model/selectors/selectMaps.js";
import "../../../shared/store/config/model/selectors/selectModels.js";
import "../../../shared/store/config/model/selectors/selectSort.js";
import "../../../shared/store/config/model/selectors/selectTags.js";
import "../../../shared/store/config/model/selectors/selectTradeIn.js";
import "../../../shared/store/config/model/slice.js";
import "../../../shared/store/installation/model/slice.js";
import "../../../shared/store/pagination/model/slice.js";
import "../../../features/filterChoice/model/middleware/index.js";
import "../../../features/filterChoice/model/selectors/selectFilterHistory.js";
import "../../../features/filterChoice/model/selectors/selectFilterSortValue.js";
import { selectFilterValues } from "../../../features/filterChoice/model/selectors/selectFilterValues.js";
import "../../../features/filterChoice/model/selectors/selectFilterValuesCount.js";
import "../../../features/filterChoice/model/selectors/selectIsFilterValuesSelected.js";
import "../../../features/filterChoice/model/selectors/selectIsInitialSortValuesSelected.js";
import "../../../features/filterChoice/model/selectors/selectIsValidSortValuesSelected.js";
import "../../../features/filterChoice/model/selectors/selectSlugedFilterValues.js";
import { actions } from "../../../features/filterChoice/model/slice.js";
import { useAvailableFilterValues } from "../../../features/filterValues/lib/api/hooks/useAvailableFilterValues.js";
import { useConstructorContext } from "../../../shared/context/constructor.js";
import "../../../shared/context/this.js";
import "@perxis-auto/shared/lib";
import { useMergedConstructorItemProps } from "../../../shared/lib/constructor/hooks/useMergedConstructorItemProps.js";
import "../../../shared/lib/constructor/mergeConstructor.js";
import "react/jsx-runtime";
import "@perxis-auto/ui-kit";
import "../../../shared/lib/logger/index.js";
import "react-router";
import "@perxis-auto/shared/lib/lib/is/isObject";
import "../../../shared/store/config/middleware/index.js";
import { GET_DEFAULT_CONTAINER_PROPS } from "../config/props.js";
import { getUserLocation } from "../lib/getUserLocation.js";
import { isLocationItem } from "../lib/isLocationItem.js";
import { useAvailableFilterLocationItem } from "../lib/useAvailableFilterLocationItem.js";
import { useAvailableLocations } from "../lib/useAvailableLocations.js";
const useContainer = (item) => {
  const props = useMergedConstructorItemProps(item, GET_DEFAULT_CONTAINER_PROPS);
  const { layout, widgets } = useConstructorContext();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(APP.name);
  const isLocationAlreadySuggested = useRef(false);
  const isReactedByUser = useRef(false);
  const hasAutoInitRunRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const [suggestedLocation, setSuggestedLocation] = useState(null);
  const filterGrid = useAppSelector(selectFilterGrid);
  const filterGridItems = useAppSelector(selectFilterGridItems);
  const { initialValues: initialFilterValues } = useAppSelector(selectFilters);
  const geo = useAppSelector(selectGeo);
  const filterValues = useAppSelector(selectFilterValues);
  const { data: filterValuesItem } = useFilterValues();
  const { data: availableFilterValuesItem } = useAvailableFilterValues();
  const availableFilterLocationItem = useAvailableFilterLocationItem(filterGrid);
  const locations = useAvailableLocations(availableFilterLocationItem, availableFilterValuesItem);
  const currentCityIds = useAppSelector((state) => {
    var _a;
    const key = availableFilterLocationItem == null ? void 0 : availableFilterLocationItem.key;
    return key ? ((_a = state.filterChoice.values) == null ? void 0 : _a[key]) ?? [] : [];
  });
  const queryParams = useQueryParams();
  const storage = StorageService.getInstance({
    storage: geo.storage,
    key: geo.key
  });
  const onSetFilterLocation = useCallback(
    (key, approvedLocation) => {
      const cityIds = (approvedLocation == null ? void 0 : approvedLocation.type) === LocationTypeEnum.Cluster ? locations.filter((location) => {
        var _a;
        return ((_a = location.cluster) == null ? void 0 : _a.id) === approvedLocation.id;
      }).map((city) => city.id) : [approvedLocation.id];
      dispatch(actions.setValues({ [key]: cityIds }));
      sessionStorage.setItem(DEFAULT_GEO_LOCATION_APPLIED_STORAGE_KEY, JSON.stringify(true));
    },
    [dispatch, locations]
  );
  const filtersQueryValues = useMemo(
    () => getFiltersQueryValues(queryParams, filterGridItems, filterValuesItem),
    [queryParams, filterGridItems, filterValuesItem]
  );
  const isQueryEmpty = Object.keys(filtersQueryValues).length === 0;
  const shouldAutoInitLocation = geo.enable && Object.keys(initialFilterValues).length === 0 && isQueryEmpty && !!availableFilterLocationItem && !!availableFilterValuesItem && ((currentCityIds == null ? void 0 : currentCityIds.length) ?? 0) === 0;
  useEffect(() => {
    const locationItem = storage.getStorageItem();
    const isLocationApplied = JSON.parse(sessionStorage.getItem(DEFAULT_GEO_LOCATION_APPLIED_STORAGE_KEY) ?? "false");
    if (!isLocationApplied && isLocationItem(locationItem) && Object.keys(filterValues).length) {
      sessionStorage.setItem(DEFAULT_GEO_LOCATION_APPLIED_STORAGE_KEY, JSON.stringify(true));
    }
  }, [currentCityIds, filterValues, storage]);
  const initLocation = useCallback(async () => {
    const locationItem = storage.getStorageItem();
    const rejectedLocationId = storage.getStorageItem(geo.rejectedKey);
    const isLocationApplied = JSON.parse(sessionStorage.getItem(DEFAULT_GEO_LOCATION_APPLIED_STORAGE_KEY) ?? "false");
    const userLocation = await getUserLocation(locations);
    if (!isDefined(locationItem) && !isDefined(userLocation)) {
      return;
    }
    if (isLocationItem(userLocation) && isDefined(rejectedLocationId) && userLocation.id === rejectedLocationId) {
      return;
    }
    if (isLocationItem(userLocation) && userLocation.id !== (locationItem == null ? void 0 : locationItem.id)) {
      setSuggestedLocation(userLocation);
      setIsVisible(true);
      isLocationAlreadySuggested.current = true;
      return;
    }
    if (isLocationItem(userLocation) && isLocationItem(locationItem) && userLocation.id === (locationItem == null ? void 0 : locationItem.id) && userLocation.type === (locationItem == null ? void 0 : locationItem.type) && isDefined(availableFilterLocationItem)) {
      if (isLocationApplied) {
        return;
      }
      const cityIdsToSet = userLocation.type === LocationTypeEnum.Cluster ? locations.filter((loc) => {
        var _a;
        return ((_a = loc.cluster) == null ? void 0 : _a.id) === userLocation.id;
      }).map((c) => c.id) : [userLocation.id];
      const isSameSelection = Array.isArray(currentCityIds) && currentCityIds.length === cityIdsToSet.length && currentCityIds.every((id, i) => id === cityIdsToSet[i]);
      if (isSameSelection) {
        return;
      }
      onSetFilterLocation(availableFilterLocationItem.key, userLocation);
    }
  }, [storage, geo.rejectedKey, locations, availableFilterLocationItem, currentCityIds, onSetFilterLocation]);
  useEffect(() => {
    if (!shouldAutoInitLocation || hasAutoInitRunRef.current) {
      return;
    }
    hasAutoInitRunRef.current = true;
    initLocation().catch((e) => console.warn("SDK:", e));
  }, [shouldAutoInitLocation, initLocation]);
  useEffect(() => {
    return () => {
      if (isLocationAlreadySuggested.current && !isReactedByUser.current && suggestedLocation) {
        storage.setStorageItem(suggestedLocation.id, geo.rejectedKey);
      }
    };
  }, [suggestedLocation, isVisible, geo.rejectedKey, storage]);
  const onSuccess = useCallback(() => {
    if (!availableFilterLocationItem || !suggestedLocation) {
      return;
    }
    onSetFilterLocation(availableFilterLocationItem.key, suggestedLocation);
    storage.setStorageItem(suggestedLocation);
    storage.removeStorageItem(geo.rejectedKey);
    setIsVisible(false);
    isReactedByUser.current = true;
  }, [availableFilterLocationItem, geo.rejectedKey, onSetFilterLocation, storage, suggestedLocation]);
  const onClose = useCallback(() => {
    storage.removeStorageItem();
    setIsVisible(false);
    isReactedByUser.current = true;
    if (suggestedLocation) {
      storage.setStorageItem(suggestedLocation.id, geo.rejectedKey);
    }
  }, [geo.rejectedKey, storage, suggestedLocation]);
  return {
    ...props,
    layout,
    widgets,
    isVisible,
    location: suggestedLocation,
    onClose,
    onSuccess,
    t
  };
};
export {
  useContainer
};
